import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Grid,
  Card,
  CardActions,
  CardContent,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  Stack,
  Box,
  Menu,
  Autocomplete,
  Paper,
  Slider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { locationsOptions } from '../utilities/constants'; // Importing predefined locations

// Example arrays for multi-select options:
const moneyEventsOptions = [
  "Inheritance",
  "Promotion",
  "Job Change",
  "Marriage",
  "Divorce",
  "Recently Moved",
  "Childbirth",
  "Reached Retirement Age",
  "Sale of Business",
  "Sale of stock",
  "Recent Fundraising Round",
  "Recent Donation"
];
const genderOptions = ['Male', 'Female'];
const educationOptions = ['High School', 'Bachelor', 'Master', 'PhD'];
const industryOptions = ['Tech', 'Finance', 'Healthcare', 'Manufacturing', 'Retail'];

function SavedProfilesPage() {
  const navigate = useNavigate();

  // All saved searches
  const [searches, setSearches] = useState([]);

  // State for the currently editing search
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editSearchData, setEditSearchData] = useState(null);
  const [totalIndustries, setTotalIndustries] = useState([]); // State for industries

    const [anchorEl, setAnchorEl] = useState(null);
  
    const handleMenuOpen = (event, list) => {
      setAnchorEl(event.currentTarget);
    };
  
    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const handleDelete = async (list) => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        await axios.delete(
          `https://api.pathvizor.com/saved_searches/${list.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // After deletion, refetch
        handleMenuClose();
        fetchSavedSearches();
      } catch (error) {
        console.error('Error deleting list:', error);
      }
    };

  // ============ FETCH SAVED SEARCHES ============

  const fetchSavedSearches = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found. Redirect or handle as needed.');
      return;
    }
    axios
    .get('https://api.pathvizor.com/saved_searches', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((response) => {
      setSearches(response.data);
    })
    .catch((error) => {
      console.error('Error fetching saved searches:', error);
    });
  };

  const fetchIndustries = async () => {
    try {
      const response = await axios.get('https://api.pathvizor.com/industries'); // Replace with your API endpoint
      const responseArray = []
      response.data.forEach((industry) => {
          responseArray.push(
              industry.name
          )})

      setTotalIndustries(responseArray)
    } catch (err) {
      console.log('Failed to fetch industries. Please try again later.');
    } 
  };




  useEffect(() => {
    fetchSavedSearches();
    fetchIndustries();
  }, []);

  // ============ OPEN EDIT DIALOG ============
  const handleOpenEditDialog = (search) => {
    setEditSearchData({
      ...search,
      // Ensure arrays are arrays, not null
      param_money_events: search.param_money_events || [],
      param_locations: search.param_locations || [],
      param_gender: search.param_gender || [],
      param_age_range: search.param_age_range || [],
      param_networth_range: search.param_networth_range || [],
      param_education: search.param_education || [],
      param_industry: search.param_industry || [],
      param_age_range: search.param_age_range || [0, 100],
      param_networth_range: search.param_networth_range || [],
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditSearchData(null);
  };

  // ============ SAVE EDIT ============
  const handleSaveEdit = async () => {
    if (!editSearchData) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      await axios.put(
        `https://api.pathvizor.com/saved_searches/${editSearchData.id}`,
        editSearchData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      // re-fetch or update state in memory
      const { data } = await axios.get('https://api.pathvizor.com/saved_searches', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSearches(data);
      handleCloseEditDialog();
    } catch (error) {
      console.error('Error saving edited search:', error);
    }
  };

  // ============ HANDLE FIELD CHANGES IN EDIT DIALOG ============
  const handleEditFieldChange = (field, value) => {
    setEditSearchData((prev) => ({ ...prev, [field]: value }));
  };

  // ============ RENDER MULTI-SELECT FOR EDITING ============
  const renderMultiSelect = (label, field, options) => (
    <FormControl fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={editSearchData[field] || []}
        onChange={(e) => handleEditFieldChange(field, e.target.value)}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => selected.join(', ')}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox checked={(editSearchData[field] || []).indexOf(option) > -1} />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // ============ REDIRECT TO LEADS PAGE ============
  // For demonstration, we pass the entire search object as route state.
  // On the leads page, you can read the filter data from `location.state`.
  const handleGoToLeads = (search) => {
    navigate('/dashboard', { state: { 
        filters: {
            education: search.param_education,
            location: search.param_locations,
            industry: search.param_industry,
            compellingEvents: search.param_money_events,
            gender: search.param_gender,
            netWorthRange: search.param_networth_range,
            ageRange: search.param_age_range,
        },
        searchFields: {
            name: search.param_name,
            job_title: search.param_job_title,
            alma_mater: search.param_alma_mater,
        } 
    } });
  };

  // ============ UI RENDER ============
  return (
    <div style={{ padding: '20px', marginTop: '64px' }} >
      <Typography variant="h4" gutterBottom>
        Saved Profiles
      </Typography>

      {/* --- Use a Grid to display cards in a neat layout --- */}
      <Grid container spacing={2}>
        {searches.map((search) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={search.id}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" gutterBottom>
                  {search.search_name}
                </Typography>
                <Button
                  onClick={(event) => handleMenuOpen(event, search)}
                >
                  •••
                </Button>
                <Menu
                  id={`menu-${search.id}`}
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem onClick={() => handleDelete(search)}>Delete</MenuItem>
                </Menu>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {search.description}
                </Typography>
              </CardContent>
              <CardActions>
                <Button variant="contained" onClick={() => handleGoToLeads(search)}>
                  Apply Filters
                </Button>
                <Button variant="text" onClick={() => handleOpenEditDialog(search)}>
                  Edit
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* --- EDIT DIALOG --- */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit Saved Search</DialogTitle>
        <DialogContent>
          {editSearchData && (
            <Stack spacing={2} marginTop={2}>
              <TextField
                label="Search Name"
                value={editSearchData.search_name || ''}
                onChange={(e) => handleEditFieldChange('search_name', e.target.value)}
                fullWidth
              />
              <TextField
                label="Description"
                value={editSearchData.description || ''}
                onChange={(e) => handleEditFieldChange('description', e.target.value)}
                fullWidth
              />
              <TextField
                label="Lead Name"
                value={editSearchData.param_name || ''}
                onChange={(e) => handleEditFieldChange('param_name', e.target.value)}
                fullWidth
              />

              {renderMultiSelect('Money Events', 'param_money_events', moneyEventsOptions)}
                      {/* Locations */}
                      <Autocomplete
                      multiple
                      options={locationsOptions}
                      getOptionLabel={(option) => option.name} // Display the full state name
                      value={editSearchData.param_locations.map((abbreviation) =>
                          locationsOptions.find((state) => state.abbreviation === abbreviation) || { name: '', abbreviation: '' }
                      )} // Map abbreviations to full state objects
                      onChange={(_, newValue) => {
                          const abbreviations = newValue.map((state) => state.abbreviation); // Store only the abbreviations
                          handleEditFieldChange('param_locations', abbreviations);
                      }}
                      renderInput={(params) => <TextField {...params} label="Locations (US States)" variant="outlined" />}
                      />
              {renderMultiSelect('Gender', 'param_gender', genderOptions)}
              {/* {renderMultiSelect('Education', 'param_education', educationOptions)} */}
              {renderMultiSelect('Industry', 'param_industry', totalIndustries)}



              <TextField
                label="Job Title"
                value={editSearchData.param_job_title || ''}
                onChange={(e) => handleEditFieldChange('param_job_title', e.target.value)}
                fullWidth
              />
              <TextField
                label="Alma Mater"
                value={editSearchData.param_alma_mater || ''}
                onChange={(e) => handleEditFieldChange('param_alma_mater', e.target.value)}
                fullWidth
              />
              <Paper elevation={2} sx={{ padding: 2 }}>
                <Typography gutterBottom>Age Range</Typography>
                <Slider
                  value={editSearchData.param_age_range.length === 2 ? editSearchData.param_age_range : [0, 100]}
                  onChange={(_, newValue) => handleEditFieldChange('param_age_range', newValue)}
                  valueLabelDisplay="auto"
                  min={0} // Allow dragging below the normal minimum
                  max={100} // Allow dragging above the normal maximum
                  marks={[
                    { value: 0, label: 'No min' }, // Special label for unbounded minimum
                    { value: 20, label: '20' },
                    { value: 40, label: '40' },
                    { value: 60, label: '60' },
                    { value: 80, label: '80' },
                    { value: 100, label: 'No max' }, // Special label for unbounded maximum
                  ]}
                />
              </Paper>
              <Paper elevation={2} sx={{ padding: 2 }}>
                <Typography gutterBottom>Net Worth</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {/* Minimum Net Worth Dropdown */}
                  <FormControl variant="outlined" size="small">
                    <InputLabel>Min</InputLabel>
                    <Select
                      value={editSearchData.param_networth_range?.[0] ?? ''}
                      onChange={(e) => {
                        const minValue = parseInt(e.target.value, 10) || 0;
                        handleEditFieldChange('param_networth_range', [minValue, editSearchData.param_networth_range?.[1] || 20000000]);
                      }}
                      label="Min"
                    >
                      <MenuItem value={0}>No Min</MenuItem>
                      <MenuItem value={50000}>$50K</MenuItem>
                      <MenuItem value={100000}>$100K</MenuItem>
                      <MenuItem value={250000}>$250K</MenuItem>
                      <MenuItem value={500000}>$500K</MenuItem>
                      <MenuItem value={1000000}>$1M</MenuItem>
                      <MenuItem value={2000000}>$2M</MenuItem>
                      <MenuItem value={5000000}>$5M</MenuItem>
                      <MenuItem value={10000000}>$10M</MenuItem>
                      <MenuItem value={15000000}>$15M</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Maximum Net Worth Dropdown */}
                  <FormControl variant="outlined" size="small">
                    <InputLabel>Max</InputLabel>
                    <Select
                      value={editSearchData.param_networth_range?.[1] || ''}
                      onChange={(e) => {
                        const maxValue = parseInt(e.target.value, 10) || 0;
                        handleEditFieldChange('param_networth_range', [editSearchData.param_networth_range?.[0] || 0, maxValue]);
                      }}
                      label="Max"
                    >
                      <MenuItem value={20000000}>No Max</MenuItem>
                      <MenuItem value={50000}>$50K</MenuItem>
                      <MenuItem value={100000}>$100K</MenuItem>
                      <MenuItem value={250000}>$250K</MenuItem>
                      <MenuItem value={500000}>$500K</MenuItem>
                      <MenuItem value={1000000}>$1M</MenuItem>
                      <MenuItem value={2000000}>$2M</MenuItem>
                      <MenuItem value={5000000}>$5M</MenuItem>
                      <MenuItem value={10000000}>$10M</MenuItem>
                      <MenuItem value={15000000}>$15M</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Paper>
            </Stack>
            
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default SavedProfilesPage;
